import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calculator, RefreshCw, AlertTriangle, Info } from 'lucide-react';

export const TdsCalculatorPage = () => {
  // TDS Rate Calculator State
  const [rateFormData, setRateFormData] = useState({
    paymentType: 'salary',
    amount: '',
    panAvailable: 'yes',
  });
  const [rateResult, setRateResult] = useState(null);

  // TDS Late Fee Calculator State
  const [feeFormData, setFeeFormData] = useState({
    tdsAmount: '',
    dueDate: '',
    actualDeductionDate: '',
    actualDepositDate: '',
    returnDueDate: '',
    returnFiledDate: '',
  });
  const [feeResult, setFeeResult] = useState(null);

  const tdsRates = {
    salary: { withPan: 'As per slab', withoutPan: 'As per slab', section: '192', threshold: 0 },
    interest: { withPan: 10, withoutPan: 20, section: '194A', threshold: 40000 },
    dividend: { withPan: 10, withoutPan: 20, section: '194', threshold: 5000 },
    rent_land: { withPan: 10, withoutPan: 20, section: '194I(a)', threshold: 240000 },
    rent_equipment: { withPan: 2, withoutPan: 20, section: '194I(b)', threshold: 240000 },
    professional: { withPan: 10, withoutPan: 20, section: '194J', threshold: 30000 },
    contractor_individual: { withPan: 1, withoutPan: 20, section: '194C', threshold: 30000 },
    contractor_others: { withPan: 2, withoutPan: 20, section: '194C', threshold: 30000 },
    commission: { withPan: 5, withoutPan: 20, section: '194H', threshold: 15000 },
    property_sale: { withPan: 1, withoutPan: 20, section: '194IA', threshold: 5000000 },
    ecommerce: { withPan: 1, withoutPan: 20, section: '194O', threshold: 500000 },
    lottery: { withPan: 30, withoutPan: 30, section: '194B', threshold: 10000 },
  };

  const paymentTypes = [
    { value: 'salary', label: 'Salary' },
    { value: 'interest', label: 'Interest (other than securities)' },
    { value: 'dividend', label: 'Dividend' },
    { value: 'rent_land', label: 'Rent - Land/Building' },
    { value: 'rent_equipment', label: 'Rent - Plant/Machinery/Equipment' },
    { value: 'professional', label: 'Professional/Technical Fees' },
    { value: 'contractor_individual', label: 'Contractor - Individual/HUF' },
    { value: 'contractor_others', label: 'Contractor - Others' },
    { value: 'commission', label: 'Commission/Brokerage' },
    { value: 'property_sale', label: 'Sale of Property (Above ₹50L)' },
    { value: 'ecommerce', label: 'E-commerce Transactions' },
    { value: 'lottery', label: 'Lottery/Puzzle Winnings' },
  ];

  // TDS Rate Calculator
  const handleRateCalculate = () => {
    const amount = parseFloat(rateFormData.amount) || 0;
    if (amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    const rateInfo = tdsRates[rateFormData.paymentType];
    const rate = rateFormData.panAvailable === 'yes' ? rateInfo.withPan : rateInfo.withoutPan;
    
    let tdsAmount = 0;
    let effectiveRate = rate;
    let isBelowThreshold = amount < rateInfo.threshold && rateInfo.threshold > 0;
    
    if (typeof rate === 'number' && !isBelowThreshold) {
      tdsAmount = (amount * rate) / 100;
    } else if (typeof rate === 'number' && isBelowThreshold) {
      tdsAmount = 0;
      effectiveRate = 0;
    } else {
      effectiveRate = 'Slab based';
    }

    setRateResult({
      amount,
      section: rateInfo.section,
      rate: effectiveRate,
      threshold: rateInfo.threshold,
      isBelowThreshold,
      tdsAmount: Math.round(tdsAmount),
      netAmount: Math.round(amount - tdsAmount),
    });
  };

  // TDS Late Fee Calculator
  const handleFeeCalculate = () => {
    const tdsAmount = parseFloat(feeFormData.tdsAmount) || 0;
    
    if (tdsAmount <= 0 || !feeFormData.dueDate || !feeFormData.actualDepositDate) {
      alert('Please enter TDS amount, due date and deposit date');
      return;
    }

    const dueDate = new Date(feeFormData.dueDate);
    const actualDeductionDate = feeFormData.actualDeductionDate ? new Date(feeFormData.actualDeductionDate) : dueDate;
    const actualDepositDate = new Date(feeFormData.actualDepositDate);
    const returnDueDate = feeFormData.returnDueDate ? new Date(feeFormData.returnDueDate) : null;
    const returnFiledDate = feeFormData.returnFiledDate ? new Date(feeFormData.returnFiledDate) : null;

    // Calculate interest for late deduction (1% per month under section 201(1A))
    let lateDeductionMonths = 0;
    let lateDeductionInterest = 0;
    if (actualDeductionDate > dueDate) {
      const diffTime = Math.abs(actualDeductionDate - dueDate);
      lateDeductionMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
      lateDeductionInterest = Math.round((tdsAmount * 1 * lateDeductionMonths) / 100);
    }

    // Calculate interest for late deposit (1.5% per month under section 201(1A))
    let lateDepositMonths = 0;
    let lateDepositInterest = 0;
    const depositDueDate = actualDeductionDate > dueDate ? actualDeductionDate : dueDate;
    if (actualDepositDate > depositDueDate) {
      const diffTime = Math.abs(actualDepositDate - depositDueDate);
      lateDepositMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
      lateDepositInterest = Math.round((tdsAmount * 1.5 * lateDepositMonths) / 100);
    }

    // Calculate late filing fee (₹200 per day under section 234E, max = TDS amount)
    let lateDays = 0;
    let lateFilingFee = 0;
    if (returnDueDate && returnFiledDate && returnFiledDate > returnDueDate) {
      const diffTime = Math.abs(returnFiledDate - returnDueDate);
      lateDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      lateFilingFee = Math.min(lateDays * 200, tdsAmount);
    }

    const totalPenalty = lateDeductionInterest + lateDepositInterest + lateFilingFee;

    setFeeResult({
      tdsAmount,
      lateDeductionMonths,
      lateDeductionInterest,
      lateDepositMonths,
      lateDepositInterest,
      lateDays,
      lateFilingFee,
      totalPenalty,
      totalPayable: tdsAmount + totalPenalty,
    });
  };

  const handleRateReset = () => {
    setRateFormData({ paymentType: 'salary', amount: '', panAvailable: 'yes' });
    setRateResult(null);
  };

  const handleFeeReset = () => {
    setFeeFormData({
      tdsAmount: '',
      dueDate: '',
      actualDeductionDate: '',
      actualDepositDate: '',
      returnDueDate: '',
      returnFiledDate: '',
    });
    setFeeResult(null);
  };

  const formatCurrency = (num) => new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0
  }).format(num);

  return (
    <div data-testid="tds-calculator-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-6">
              <Calculator className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              TDS Calculator <span className="text-gradient">FY 2024-25</span>
            </h1>
            <p className="text-slate-400">
              Calculate TDS on various payments and late fee/interest for delayed TDS compliance.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Tabs */}
      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Tabs defaultValue="rate" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-navy-800 border border-white/10 rounded-xl p-1 mb-8">
              <TabsTrigger 
                value="rate" 
                data-testid="tab-rate"
                className="rounded-lg data-[state=active]:bg-blue-500 data-[state=active]:text-white text-slate-400"
              >
                TDS Rate Calculator
              </TabsTrigger>
              <TabsTrigger 
                value="fee"
                data-testid="tab-fee"
                className="rounded-lg data-[state=active]:bg-blue-500 data-[state=active]:text-white text-slate-400"
              >
                TDS Late Fee & Interest
              </TabsTrigger>
            </TabsList>

            {/* TDS Rate Calculator Tab */}
            <TabsContent value="rate">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-navy-800 border border-white/10 rounded-2xl p-6 md:p-8">
                  <h2 className="font-heading font-semibold text-xl text-white mb-6">Calculate TDS on Payment</h2>
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Nature of Payment</Label>
                      <Select value={rateFormData.paymentType} onValueChange={(v) => setRateFormData({...rateFormData, paymentType: v})}>
                        <SelectTrigger data-testid="select-payment-type" className="bg-navy-900 border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-navy-800 border-white/10 max-h-80">
                          {paymentTypes.map(type => (
                            <SelectItem key={type.value} value={type.value} className="text-white hover:bg-navy-700">
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Payment Amount (₹)</Label>
                      <Input
                        type="number"
                        data-testid="input-amount"
                        value={rateFormData.amount}
                        onChange={(e) => setRateFormData({...rateFormData, amount: e.target.value})}
                        placeholder="Enter payment amount"
                        className="bg-navy-900 border-white/10 text-white text-lg"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Is PAN Available?</Label>
                      <Select value={rateFormData.panAvailable} onValueChange={(v) => setRateFormData({...rateFormData, panAvailable: v})}>
                        <SelectTrigger data-testid="select-pan" className="bg-navy-900 border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-navy-800 border-white/10">
                          <SelectItem value="yes" className="text-white hover:bg-navy-700">Yes</SelectItem>
                          <SelectItem value="no" className="text-white hover:bg-navy-700">No (20% TDS applies)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-4 pt-4">
                      <Button data-testid="calculate-rate" onClick={handleRateCalculate} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-6">
                        <Calculator className="mr-2 w-5 h-5" />Calculate TDS
                      </Button>
                      <Button onClick={handleRateReset} variant="outline" className="border-white/20 text-white hover:bg-white/10 py-6">
                        <RefreshCw className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="bg-navy-800 border border-white/10 rounded-2xl p-6 md:p-8">
                  {rateResult ? (
                    <div data-testid="rate-result">
                      <h2 className="font-heading font-semibold text-xl text-white mb-6">TDS Calculation Result</h2>
                      <div className="space-y-4">
                        <div className="flex justify-between py-2 border-b border-white/10">
                          <span className="text-slate-400">Payment Amount</span>
                          <span className="text-white">{formatCurrency(rateResult.amount)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-white/10">
                          <span className="text-slate-400">Section</span>
                          <span className="text-blue-400 font-medium">{rateResult.section}</span>
                        </div>
                        {rateResult.threshold > 0 && (
                          <div className="flex justify-between py-2 border-b border-white/10">
                            <span className="text-slate-400">Threshold Limit</span>
                            <span className="text-white">{formatCurrency(rateResult.threshold)}</span>
                          </div>
                        )}
                        <div className="flex justify-between py-2 border-b border-white/10">
                          <span className="text-slate-400">TDS Rate</span>
                          <span className="text-white">{typeof rateResult.rate === 'number' ? `${rateResult.rate}%` : rateResult.rate}</span>
                        </div>
                        {rateResult.isBelowThreshold && (
                          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-start gap-2">
                            <Info className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                            <p className="text-green-400 text-sm">Amount is below threshold limit. No TDS applicable.</p>
                          </div>
                        )}
                        <div className="flex justify-between py-2 border-b border-white/10">
                          <span className="text-slate-400">TDS Amount</span>
                          <span className="text-red-400 font-semibold">{formatCurrency(rateResult.tdsAmount)}</span>
                        </div>
                        <div className="flex justify-between py-3 bg-blue-500/10 rounded-lg px-4 mt-4">
                          <span className="text-white font-medium">Net Amount Payable</span>
                          <span className="text-blue-400 font-bold text-lg">{formatCurrency(rateResult.netAmount)}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
                      <Calculator className="w-16 h-16 text-slate-600 mb-4" />
                      <h3 className="font-heading font-semibold text-xl text-white mb-2">Enter Payment Details</h3>
                      <p className="text-slate-400 text-center">Fill in the details to calculate TDS on your payment</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* TDS Late Fee Calculator Tab */}
            <TabsContent value="fee">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-navy-800 border border-white/10 rounded-2xl p-6 md:p-8">
                  <h2 className="font-heading font-semibold text-xl text-white mb-6">TDS Late Fee & Interest</h2>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">TDS Amount (₹)</Label>
                      <Input
                        type="number"
                        data-testid="input-tds-amount"
                        value={feeFormData.tdsAmount}
                        onChange={(e) => setFeeFormData({...feeFormData, tdsAmount: e.target.value})}
                        placeholder="Enter TDS amount"
                        className="bg-navy-900 border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Due Date (for Deduction)</Label>
                      <Input
                        type="date"
                        data-testid="input-due-date"
                        value={feeFormData.dueDate}
                        onChange={(e) => setFeeFormData({...feeFormData, dueDate: e.target.value})}
                        className="bg-navy-900 border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Actual Deduction Date</Label>
                      <Input
                        type="date"
                        data-testid="input-deduction-date"
                        value={feeFormData.actualDeductionDate}
                        onChange={(e) => setFeeFormData({...feeFormData, actualDeductionDate: e.target.value})}
                        className="bg-navy-900 border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Actual Deposit Date</Label>
                      <Input
                        type="date"
                        data-testid="input-deposit-date"
                        value={feeFormData.actualDepositDate}
                        onChange={(e) => setFeeFormData({...feeFormData, actualDepositDate: e.target.value})}
                        className="bg-navy-900 border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Return Due Date (Optional)</Label>
                      <Input
                        type="date"
                        data-testid="input-return-due"
                        value={feeFormData.returnDueDate}
                        onChange={(e) => setFeeFormData({...feeFormData, returnDueDate: e.target.value})}
                        className="bg-navy-900 border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Return Filed Date (Optional)</Label>
                      <Input
                        type="date"
                        data-testid="input-return-filed"
                        value={feeFormData.returnFiledDate}
                        onChange={(e) => setFeeFormData({...feeFormData, returnFiledDate: e.target.value})}
                        className="bg-navy-900 border-white/10 text-white"
                      />
                    </div>
                    <div className="flex gap-4 pt-4">
                      <Button data-testid="calculate-fee" onClick={handleFeeCalculate} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-6">
                        <Calculator className="mr-2 w-5 h-5" />Calculate
                      </Button>
                      <Button onClick={handleFeeReset} variant="outline" className="border-white/20 text-white hover:bg-white/10 py-6">
                        <RefreshCw className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="bg-navy-800 border border-white/10 rounded-2xl p-6 md:p-8">
                  {feeResult ? (
                    <div data-testid="fee-result">
                      <h2 className="font-heading font-semibold text-xl text-white mb-6">Late Fee & Interest Calculation</h2>
                      <div className="space-y-4">
                        <div className="flex justify-between py-2 border-b border-white/10">
                          <span className="text-slate-400">TDS Amount</span>
                          <span className="text-white">{formatCurrency(feeResult.tdsAmount)}</span>
                        </div>
                        
                        {feeResult.lateDeductionMonths > 0 && (
                          <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3">
                            <p className="text-orange-400 text-sm font-medium mb-1">Late Deduction Interest (Sec 201(1A))</p>
                            <div className="flex justify-between">
                              <span className="text-slate-400 text-sm">{feeResult.lateDeductionMonths} month(s) @ 1%/month</span>
                              <span className="text-orange-400">{formatCurrency(feeResult.lateDeductionInterest)}</span>
                            </div>
                          </div>
                        )}

                        {feeResult.lateDepositMonths > 0 && (
                          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                            <p className="text-red-400 text-sm font-medium mb-1">Late Deposit Interest (Sec 201(1A))</p>
                            <div className="flex justify-between">
                              <span className="text-slate-400 text-sm">{feeResult.lateDepositMonths} month(s) @ 1.5%/month</span>
                              <span className="text-red-400">{formatCurrency(feeResult.lateDepositInterest)}</span>
                            </div>
                          </div>
                        )}

                        {feeResult.lateDays > 0 && (
                          <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                            <p className="text-purple-400 text-sm font-medium mb-1">Late Filing Fee (Sec 234E)</p>
                            <div className="flex justify-between">
                              <span className="text-slate-400 text-sm">{feeResult.lateDays} day(s) @ ₹200/day</span>
                              <span className="text-purple-400">{formatCurrency(feeResult.lateFilingFee)}</span>
                            </div>
                          </div>
                        )}

                        <div className="flex justify-between py-2 border-b border-white/10">
                          <span className="text-slate-400">Total Interest & Fee</span>
                          <span className="text-red-400 font-semibold">{formatCurrency(feeResult.totalPenalty)}</span>
                        </div>

                        <div className="flex justify-between py-3 bg-red-500/10 rounded-lg px-4 mt-4">
                          <span className="text-white font-medium">Total Amount Payable</span>
                          <span className="text-red-400 font-bold text-lg">{formatCurrency(feeResult.totalPayable)}</span>
                        </div>

                        {feeResult.totalPenalty === 0 && (
                          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                            <p className="text-green-400 font-medium">No late fee or interest applicable!</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
                      <Calculator className="w-16 h-16 text-slate-600 mb-4" />
                      <h3 className="font-heading font-semibold text-xl text-white mb-2">Enter TDS Details</h3>
                      <p className="text-slate-400 text-center">Calculate interest and late fee for delayed TDS compliance</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Interest & Fee Rules */}
      <section className="py-12 md:py-16 bg-navy-800/50">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="font-heading font-bold text-2xl text-white text-center mb-8">TDS Interest & Penalty Rules</h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-navy-800 border border-orange-500/20 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-orange-400 mb-3">Late Deduction</h3>
              <p className="text-white text-2xl font-bold mb-2">1% per month</p>
              <p className="text-slate-400 text-sm">Interest under Section 201(1A) for not deducting TDS on time</p>
            </div>
            <div className="bg-navy-800 border border-red-500/20 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-red-400 mb-3">Late Deposit</h3>
              <p className="text-white text-2xl font-bold mb-2">1.5% per month</p>
              <p className="text-slate-400 text-sm">Interest under Section 201(1A) for late deposit of TDS to government</p>
            </div>
            <div className="bg-navy-800 border border-purple-500/20 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-purple-400 mb-3">Late Filing</h3>
              <p className="text-white text-2xl font-bold mb-2">₹200 per day</p>
              <p className="text-slate-400 text-sm">Fee under Section 234E (max = TDS amount)</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0" />
              <div>
                <h3 className="font-heading font-semibold text-lg text-white mb-2">Important Notes</h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li>• Interest is calculated from the date of deduction/collection to the date of actual deposit</li>
                  <li>• Part of a month is considered as a full month for interest calculation</li>
                  <li>• Late filing fee is capped at the TDS amount itself</li>
                  <li>• Non-filing of TDS returns can result in penalty up to ₹1 lakh under Section 271H</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TDS Rate Chart */}
      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="font-heading font-bold text-2xl text-white text-center mb-8">TDS Rate Chart FY 2024-25</h2>
          <div className="bg-navy-800 border border-white/5 rounded-xl overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy-900/50 border-b border-white/5">
                  <th className="text-left p-4 text-white font-medium">Section</th>
                  <th className="text-left p-4 text-white font-medium">Nature of Payment</th>
                  <th className="text-center p-4 text-white font-medium">Threshold</th>
                  <th className="text-center p-4 text-white font-medium">With PAN</th>
                  <th className="text-center p-4 text-white font-medium">Without PAN</th>
                </tr>
              </thead>
              <tbody>
                {paymentTypes.map((type) => {
                  const rate = tdsRates[type.value];
                  return (
                    <tr key={type.value} className="border-b border-white/5 hover:bg-navy-900/30">
                      <td className="p-4 text-blue-400 font-medium">{rate.section}</td>
                      <td className="p-4 text-slate-300">{type.label}</td>
                      <td className="p-4 text-center text-slate-400">
                        {rate.threshold > 0 ? `₹${(rate.threshold/100000).toFixed(1)}L` : '-'}
                      </td>
                      <td className="p-4 text-center text-white">
                        {typeof rate.withPan === 'number' ? `${rate.withPan}%` : rate.withPan}
                      </td>
                      <td className="p-4 text-center text-red-400">{rate.withoutPan}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-navy-800/50 border border-white/5 rounded-xl p-4">
            <p className="text-slate-500 text-xs text-center">
              <strong>Disclaimer:</strong> This calculator provides an estimate based on current TDS rules. 
              Actual calculations may vary. Please consult a tax professional for accurate calculations.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
